import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { RequireRole } from '@/components/guards/RequireRole';

// ─── Lazy imports — code-split per route to keep POS bundle thin (spec §4.5) ──
const LoginPage            = lazy(() => import('./features/auth/LoginPage'));
const Unauthorized         = lazy(() => import('./features/auth/Unauthorized'));
const PosPage              = lazy(() => import('./features/pos/PosPage'));
const PosHub                = lazy(() => import('./features/pos/PosHub'));
const KdsPage              = lazy(() => import('./features/kds/KdsPage'));
const BarBoard             = lazy(() => import('./features/kds/BarBoard'));
const AdminShell           = lazy(() => import('./features/admin/AdminShell'));
const AdminDashboard       = lazy(() => import('./features/admin/Dashboard'));
const AdminBarDashboard    = lazy(() => import('./features/admin/dashboards/BarDashboard').then((m) => ({ default: m.BarDashboard })));
const AdminOrders          = lazy(() => import('./features/admin/Orders'));
const AdminMenu            = lazy(() => import('./features/admin/Menu'));
const AdminBranches        = lazy(() => import('./features/admin/Branches'));
const AdminEmployees       = lazy(() => import('./features/admin/Employees'));
const AdminCustomers       = lazy(() => import('./features/admin/Customers'));
const AdminBulkSms         = lazy(() => import('./features/admin/BulkSms'));
const AdminCreditAccounts  = lazy(() => import('./features/admin/CreditAccounts'));
const AdminAddCompany      = lazy(() => import('./features/admin/AddCompany'));
const AdminCreditAccountDetail = lazy(() => import('./features/admin/CreditAccountDetail'));
const AdminCreditOrders    = lazy(() => import('./features/admin/CreditOrders'));
const AdminDeliveryOrders  = lazy(() => import('./features/admin/DeliveryOrders'));
const AdminSpecialOrders   = lazy(() => import('./features/admin/SpecialOrders'));
const AdminAttendance      = lazy(() => import('./features/admin/Attendance'));
const AdminExpenses        = lazy(() => import('./features/admin/Expenses'));
const AdminProcurement     = lazy(() => import('./features/admin/Procurement'));
const AdminInvestors       = lazy(() => import('./features/admin/Investors'));
const AdminSettings        = lazy(() => import('./features/admin/Settings'));
const AdminInventory       = lazy(() => import('./features/admin/Inventory'));
const AdminAnalytics       = lazy(() => import('./features/admin/Analytics'));
const InvestorDashboard    = lazy(() => import('./features/investor/InvestorDashboard'));
const ReportsShell         = lazy(() => import('./features/admin/reports/ReportsShell'));
const ProfitLoss           = lazy(() => import('./features/admin/reports/ProfitLoss'));
const BalanceSheet         = lazy(() => import('./features/admin/reports/BalanceSheet'));
const CashFlow             = lazy(() => import('./features/admin/reports/CashFlow'));
const TrialBalance         = lazy(() => import('./features/admin/reports/TrialBalance'));
const OrdersReport         = lazy(() => import('./features/admin/reports/OrdersReport'));
const ExpensesReport       = lazy(() => import('./features/admin/reports/ExpensesReport'));
const ProcurementReport    = lazy(() => import('./features/admin/reports/ProcurementReport'));
const InventoryReport      = lazy(() => import('./features/admin/reports/InventoryReport'));
const CustomersReport      = lazy(() => import('./features/admin/reports/CustomersReport'));
const InvestorsReport      = lazy(() => import('./features/admin/reports/InvestorsReport'));
const EmployeesReport      = lazy(() => import('./features/admin/reports/EmployeesReport'));
const RecordsPage          = lazy(() => import('./features/admin/records/RecordsPage'));
const AdminAuditLog        = lazy(() => import('./features/admin/AuditLog'));
const Discover             = lazy(() => import('./features/public/Discover'));
const PublicMenu           = lazy(() => import('./features/public/PublicMenu'));
const FeedbackForm         = lazy(() => import('./features/public/FeedbackForm'));
const AdminFeedback        = lazy(() => import('./features/admin/Feedback'));
const AdminCommission      = lazy(() => import('./features/admin/Commission'));
const SuperAdminCommission = lazy(() => import('./features/super-admin/SuperAdminCommission'));
const SuperAdminUsers      = lazy(() => import('./features/super-admin/SuperAdminUsers'));
const SuperAdminAccess     = lazy(() => import('./features/super-admin/SuperAdminAccess'));
const SuperAdminSettings   = lazy(() => import('./features/super-admin/SuperAdminSettings'));
const SuperAdminFeedback   = lazy(() => import('./features/super-admin/SuperAdminFeedback'));
const SuperAdminShell      = lazy(() => import('./features/super-admin/SuperAdminShell'));
const SuperAdminDashboard  = lazy(() => import('./features/super-admin/SuperAdminDashboard'));
const SuperAdminTenants    = lazy(() => import('./features/super-admin/SuperAdminTenants'));
const SuperAdminBranches   = lazy(() => import('./features/super-admin/SuperAdminBranches'));
const SuperAdminInvestors  = lazy(() => import('./features/super-admin/SuperAdminInvestors'));
const SuperAdminActivity   = lazy(() => import('./features/super-admin/SuperAdminActivity'));

const L = ({ children }: { children: ReactNode }) => (
  <Suspense
    fallback={
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="flex items-center gap-3 text-fg-muted">
          <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse" />
          <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse [animation-delay:120ms]" />
          <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse [animation-delay:240ms]" />
        </div>
      </div>
    }
  >
    {children}
  </Suspense>
);

/** Compose RequireRole around a lazy element. */
const Guarded = ({ children }: { children: ReactNode }) => (
  <L><RequireRole>{children}</RequireRole></L>
);

export const router = createBrowserRouter([
  { path: '/',      element: <Navigate to="/admin" replace /> },
  { path: '/login', element: <L><LoginPage /></L> },
  { path: '/403',   element: <L><Unauthorized /></L> },

  // Public — anyone can hit these without auth (used by QR scans).
  { path: '/discover',                       element: <L><Discover /></L> },
  { path: '/m/:tenantSlug/:branchId',        element: <L><PublicMenu /></L> },
  { path: '/feedback/:tenantSlug/:branchId', element: <L><FeedbackForm /></L> },

  // POS / KDS — full-screen, no admin shell. /pos is the hub for choosing
  // an order channel; Credit/Delivery/Special are separate screens from
  // the main Counter (dine-in/takeaway) flow, not tabs within one page.
  { path: '/pos',          element: <Guarded><PosHub /></Guarded> },
  { path: '/pos/counter',  element: <Guarded><PosPage /></Guarded> },
  { path: '/pos/credit',   element: <Guarded><PosPage /></Guarded> },
  { path: '/pos/delivery', element: <Guarded><PosPage /></Guarded> },
  { path: '/pos/special',  element: <Guarded><PosPage /></Guarded> },
  { path: '/pos/bar',      element: <Guarded><PosPage /></Guarded> },
  { path: '/kitchen', element: <Guarded><KdsPage /></Guarded> },
  // The bar's own live ticket board — mirrors /kitchen but for
  // kitchen_station='bar' items. Bar staff land here on login.
  { path: '/bar',     element: <Guarded><BarBoard /></Guarded> },

  // Platform admin — nested under super shell. Guard at the shell catches
  // every child path, plus ACCESS map gives per-page granularity.
  {
    path: '/super',
    element: <Guarded><SuperAdminShell /></Guarded>,
    children: [
      { index: true,         element: <L><SuperAdminDashboard /></L> },
      { path: 'tenants',     element: <L><SuperAdminTenants /></L> },
      { path: 'branches',    element: <L><SuperAdminBranches /></L> },
      { path: 'investors',   element: <L><SuperAdminInvestors /></L> },
      { path: 'commission',  element: <L><SuperAdminCommission /></L> },
      { path: 'users',       element: <L><SuperAdminUsers /></L> },
      { path: 'access',      element: <L><SuperAdminAccess /></L> },
      { path: 'settings',    element: <L><SuperAdminSettings /></L> },
      { path: 'feedback',    element: <L><SuperAdminFeedback /></L> },
      { path: 'activity',    element: <L><SuperAdminActivity /></L> },
    ],
  },

  // Investor portal.
  { path: '/investor', element: <Guarded><InvestorDashboard /></Guarded> },

  // Admin — nested under the shared shell.
  {
    path: '/admin',
    element: <Guarded><AdminShell /></Guarded>,
    children: [
      { index: true,         element: <L><AdminDashboard /></L> },
      { path: 'bar',         element: <L><AdminBarDashboard /></L> },
      { path: 'orders',      element: <L><AdminOrders /></L> },
      { path: 'menu',        element: <L><AdminMenu /></L> },
      { path: 'feedback',    element: <L><AdminFeedback /></L> },
      { path: 'branches',    element: <L><AdminBranches /></L> },
      { path: 'employees',   element: <L><AdminEmployees /></L> },
      { path: 'customers',       element: <L><AdminCustomers /></L> },
      { path: 'bulk-sms',        element: <L><AdminBulkSms /></L> },
      { path: 'credit-accounts',     element: <L><AdminCreditAccounts /></L> },
      { path: 'credit-accounts/new', element: <L><AdminAddCompany /></L> },
      { path: 'credit-accounts/:id', element: <L><AdminCreditAccountDetail /></L> },
      { path: 'credit-orders',   element: <L><AdminCreditOrders /></L> },
      { path: 'delivery-orders', element: <L><AdminDeliveryOrders /></L> },
      { path: 'special-orders',  element: <L><AdminSpecialOrders /></L> },
      { path: 'attendance',  element: <L><AdminAttendance /></L> },
      { path: 'expenses',    element: <L><AdminExpenses /></L> },
      { path: 'procurement', element: <L><AdminProcurement /></L> },
      { path: 'investors',   element: <L><AdminInvestors /></L> },
      { path: 'commission',  element: <L><AdminCommission /></L> },
      {
        path: 'reports',
        element: <L><ReportsShell /></L>,
        children: [
          { index: true,             element: <L><ProfitLoss /></L> },
          { path: 'balance-sheet',   element: <L><BalanceSheet /></L> },
          { path: 'cash-flow',       element: <L><CashFlow /></L> },
          { path: 'trial-balance',   element: <L><TrialBalance /></L> },
          { path: 'orders',          element: <L><OrdersReport /></L> },
          { path: 'expenses',        element: <L><ExpensesReport /></L> },
          { path: 'procurement',     element: <L><ProcurementReport /></L> },
          { path: 'inventory',       element: <L><InventoryReport /></L> },
          { path: 'customers',       element: <L><CustomersReport /></L> },
          { path: 'investors',       element: <L><InvestorsReport /></L> },
          { path: 'employees',       element: <L><EmployeesReport /></L> },
        ],
      },
      { path: 'settings',    element: <L><AdminSettings /></L> },

      // Stubs — covered by the architecture, UI on the roadmap.
      { path: 'analytics', element: <L><AdminAnalytics /></L> },
      { path: 'inventory', element: <L><AdminInventory /></L> },
      { path: 'records',   element: <L><RecordsPage /></L> },
      { path: 'audit-log', element: <L><AdminAuditLog /></L> },
    ],
  },

  { path: '*', element: <div className="min-h-screen grid place-items-center text-fg-muted">Not found.</div> },
]);
